import { TenantTerm } from './customerType';

/**
 * Nyckelregister at `keyRegister/` (admin-only read and write, instance-wide).
 * `types/{typeKey}` are key types ("Port + tvättstuga hus B"); `keys/{keyKey}`
 * are the individual keys (exemplar), each with a denormalised status/holder
 * and an append-only `log/{eventKey}` (write-once by rule). Counts are derived
 * in `Helpers/keyRegisterUtils.ts`, never stored.
 * Spec: docs/specs/key-register.md in mf-workspace (v1, 2026-10-08).
 */

/** Gates the block/unblock events: only tags (passerbrickor) can be blocked. */
export type KeyKind = 'mechanical' | 'tag';

export type KeyStatus = 'inStock' | 'handedOut' | 'lost' | 'blocked' | 'scrapped';

export type KeyEventType =
    | 'created'
    | 'handedOut'
    | 'returned'
    | 'lost'
    | 'found'
    | 'blocked'
    | 'unblocked'
    | 'scrapped'
    /** A free-text note in the history; changes neither status, holder nor due date. */
    | 'comment';

/** Every event except `created`, which is written only with the key itself. */
export type RecordableKeyEventType = Exclude<KeyEventType, 'created'>;

export type KeyHolderKind = 'tenant' | 'user' | 'external';

/** A real person or company: `key` is a tenantKey, an app user UID or an external user UID. */
export interface KeyHolder {
    kind: KeyHolderKind;
    key: string;
}

export type KeyReceiptKind = 'signature' | 'scan';

/** One file per event, on `handedOut` and `returned` only. Storage: `keyRegister/{keyKey}/{eventKey}/{fileName}`. */
export interface KeyReceipt {
    kind: KeyReceiptKind;
    storagePath: string;
    author: string;     // UID
    date: string;       // Stockholm 'YYYY-MM-DD hh:mm:ss'
    width: number;      // 0 for a PDF
    height: number;     // 0 for a PDF
    fileSize: number;   // bytes
}

/**
 * A file on a key type (locksmith certificate, key card, photo of the key):
 * `types/{typeKey}/attachments/{attachmentKey}`, Storage
 * `keyRegister/types/{typeKey}/attachments/{attachmentKey}/{fileName}`. Added and
 * soft-deleted on web; iOS shows them read-only. The register-wide Låsschema files
 * (`lockSchedules/{fileKey}`, Storage `keyRegister/lockSchedules/{fileKey}/{fileName}`)
 * have the same shape.
 */
export interface KeyTypeAttachmentData {
    fileName: string;
    /** Empty when the browser did not know the type. */
    mimeType: string;
    storagePath: string;
    author: string;     // UID
    date: string;       // Stockholm 'YYYY-MM-DD hh:mm:ss'
    width: number;      // 0 for a non-image
    height: number;     // 0 for a non-image
    fileSize: number;   // bytes
}

export interface KeyTypeAttachment extends KeyTypeAttachmentData {
    key: string;
}

export interface KeyEventData {
    type: KeyEventType;
    actor: string;      // UID
    when: string;       // Stockholm 'YYYY-MM-DD hh:mm:ss', when it was recorded
    /**
     * YYYY-MM-DD, the day it happened: today, or an earlier day picked on a hand-out or return.
     * Written on every event; the parser falls back to `when`'s day for events written before it.
     */
    effectiveDate: string;
    /** Required on `comment`, optional otherwise. */
    note?: string;
    /** Who received it (`handedOut`), or who had it when it left their hands (`returned`, `lost`, `blocked`, `scrapped`). */
    holder?: KeyHolder;
    /** YYYY-MM-DD, on `handedOut` only. */
    dueDate?: string;
    receipt?: KeyReceipt;
}

export interface KeyEvent extends KeyEventData {
    key: string;
}

/**
 * Objects a key type opens, keyed by the object's key with its building key as
 * the value, so readers find the object without scanning every building. A
 * deleted object leaves a dangling key that readers skip.
 */
export type KeyTypeObjectLinks = { [objectKey: string]: string };

export interface KeyTypeData {
    name: string;
    kind: KeyKind;
    description?: string;
    /** Locksmith's system/order number. */
    lockSystemRef?: string;
    /** Buildings this type opens as a whole (port, tvättstuga); drives the building filter. */
    opensBuildings?: { [buildingKey: string]: true };
    /** Lägenheter and lokaler (`buildings/{b}/units/{u}`) this type opens: unitKey → its buildingKey. */
    opensUnits?: KeyTypeObjectLinks;
    /** Förråd (`buildings/{b}/storageUnits/{s}`) this type opens: storageUnitKey → its buildingKey. */
    opensStorageUnits?: KeyTypeObjectLinks;
    /** Free text: "Port, tvättstuga, förråd". */
    opens?: string;
    archived?: true;
    /** Keyed by attachmentKey; written only by the attachment methods, never by a type edit. */
    attachments?: { [attachmentKey: string]: KeyTypeAttachmentData };
    createdBy: string;
    createdWhen: string;
    updatedBy?: string;
    updatedWhen?: string;
}

export interface KeyType extends KeyTypeData {
    key: string;
}

export interface KeyTypeInput {
    name: string;
    kind: KeyKind;
    description?: string;
    lockSystemRef?: string;
    opensBuildings?: string[];
    opensUnits?: KeyTypeObjectLinks;
    opensStorageUnits?: KeyTypeObjectLinks;
    opens?: string;
}

/** The stored key node, without its log. */
export interface RegisterKeyData {
    typeKey: string;
    serial?: string;
    /** Where it is kept when in stock: "Nyckelskåp A, krok 12". */
    location?: string;
    status: KeyStatus;
    /** Present only while `handedOut`. */
    holder?: KeyHolder;
    /** YYYY-MM-DD, present only on a hand-out with a return date. */
    dueDate?: string;
    createdBy: string;
    createdWhen: string;
    updatedBy?: string;
    updatedWhen?: string;
}

/** A key (exemplar). Named RegisterKey to stay clear of React's `key` and TS's `keyof`. */
export interface RegisterKey extends RegisterKeyData {
    key: string;
    /** Oldest first. */
    log: KeyEvent[];
}

/** The parts of a key a transition reads and writes. */
export type KeyState = Pick<RegisterKeyData, 'status' | 'holder' | 'dueDate'>;

export interface KeyRegisterData {
    types: KeyType[];
    keys: RegisterKey[];
    /** Låsschema: register-wide files, newest first. */
    lockSchedules: KeyTypeAttachment[];
}

export interface KeyFieldsInput {
    serial?: string;
    location?: string;
}

export interface KeyEventInput {
    type: RecordableKeyEventType;
    /** Required on `handedOut`; ignored otherwise (the key's current holder is copied to the event). */
    holder?: KeyHolder;
    /** YYYY-MM-DD, `handedOut` only. */
    dueDate?: string;
    /** YYYY-MM-DD, `handedOut` and `returned` only; default today. Within `effectiveDateRange`. */
    effectiveDate?: string;
    /** Required (non-empty) on `comment`. */
    note?: string;
    /** `handedOut` and `returned` only. Image or PDF. */
    receiptFile?: File;
    /** Default `scan` (the web has no signature drawing). */
    receiptKind?: KeyReceiptKind;
}

/** "Lämna ut nycklar": one hand-out of several in-stock keys to one holder, sharing every field. */
export type KeyHandOutInput = Pick<KeyEventInput, 'dueDate' | 'effectiveDate' | 'note' | 'receiptFile' | 'receiptKind'> & {
    holder: KeyHolder;
};

export const KEY_KINDS: KeyKind[] = ['mechanical', 'tag'];
export const KEY_STATUSES: KeyStatus[] = ['inStock', 'handedOut', 'lost', 'blocked', 'scrapped'];
export const KEY_EVENT_TYPES: KeyEventType[] = ['created', 'handedOut', 'returned', 'lost', 'found', 'blocked', 'unblocked', 'scrapped', 'comment'];
export const KEY_HOLDER_KINDS: KeyHolderKind[] = ['tenant', 'user', 'external'];

export const KEY_KIND_LABELS: Record<KeyKind, string> = {
    mechanical: 'Mekanisk nyckel',
    tag: 'Tagg/passerbricka',
};

export const KEY_STATUS_LABELS: Record<KeyStatus, string> = {
    inStock: 'Inne',
    handedOut: 'Ute',
    lost: 'Försvunnen',
    blocked: 'Spärrad',
    scrapped: 'Kasserad',
};

/** "Utlånad" for a hand-out with a return date, else the plain status label. */
export const keyStatusLabel = (key: Pick<RegisterKeyData, 'status' | 'dueDate'>): string =>
    key.status === 'handedOut' && key.dueDate ? 'Utlånad' : KEY_STATUS_LABELS[key.status];

/** Log entry labels (what happened). */
export const KEY_EVENT_LABELS: Record<KeyEventType, string> = {
    created: 'Skapad',
    handedOut: 'Utlämnad',
    returned: 'Återlämnad',
    lost: 'Försvunnen',
    found: 'Återfunnen',
    blocked: 'Spärrad',
    unblocked: 'Spärr hävd',
    scrapped: 'Kasserad',
    comment: 'Kommentar',
};

/** "Utlånad" in the log for a hand-out with a return date. */
export const keyEventLabel = (event: Pick<KeyEventData, 'type' | 'dueDate'>): string =>
    event.type === 'handedOut' && event.dueDate ? 'Utlånad' : KEY_EVENT_LABELS[event.type];

/** Button labels for recording an event. */
export const KEY_EVENT_ACTION_LABELS: Record<RecordableKeyEventType, string> = {
    handedOut: 'Lämna ut',
    returned: 'Återlämna',
    lost: 'Markera som försvunnen',
    found: 'Markera som återfunnen',
    blocked: 'Spärra',
    unblocked: 'Häv spärr',
    scrapped: 'Kassera',
    comment: 'Lägg till kommentar',
};

/** Bulk-create cap for "Lägg till exemplar" (API and form). */
export const MAX_KEYS_PER_BATCH = 200;

/** Max length of an event note or comment (UI validation). */
export const KEY_NOTE_MAX_LENGTH = 500;

export const KEY_HOLDER_KIND_LABELS: Record<KeyHolderKind, string> = {
    tenant: 'Hyresgäst',
    user: 'Användare',
    external: 'Extern användare',
};

/** A holder kind's label, with the instance's word for tenants ("Medlem / hyresgäst" in a BRF). */
export const keyHolderKindLabel = (kind: KeyHolderKind, tenant: TenantTerm): string =>
    kind === 'tenant' ? tenant.singular : KEY_HOLDER_KIND_LABELS[kind];

